import { z } from 'zod';

/**
 * Конструктор отчётов: срезы по трём измерениям.
 *
 * Белый список полей — не ограничение ради ограничения: имена измерений
 * и мер попадают прямо в SQL, и свободный ввод здесь означал бы
 * дыру размером с базу данных.
 */
export const Dimension = z.enum([
  'day',        // дата
  'week',       // неделя
  'month',      // месяц
  'weekday',    // день недели
  'hour',       // час
  'location',   // точка
  'category',   // категория меню
  'product',    // блюдо
  'cashier',    // кто закрыл чек
  'waiter',     // кто открыл заказ
  'payMethod',  // способ оплаты
  'orderMode',  // зал / с собой / доставка
  'table',      // стол
]);
export type Dimension = z.infer<typeof Dimension>;

export const Measure = z.enum([
  'revenue',    // выручка
  'checks',     // чеков
  'avgCheck',   // средний чек
  'qty',        // порций
  'discount',   // скидка
  'cost',       // себестоимость
  'margin',     // маржа
  'marginPct',  // маржинальность, %
  'share',      // доля в выручке, %
]);
export type Measure = z.infer<typeof Measure>;

/** Человеческие названия — одни и те же на экране, в выгрузке и в шаблоне. */
export const DIMENSION_LABEL: Record<Dimension, string> = {
  day: 'Дата', week: 'Неделя', month: 'Месяц', weekday: 'День недели',
  hour: 'Час', location: 'Точка', category: 'Категория', product: 'Блюдо',
  cashier: 'Кассир', waiter: 'Официант', payMethod: 'Оплата',
  orderMode: 'Тип заказа', table: 'Стол',
};

export const MEASURE_LABEL: Record<Measure, string> = {
  revenue: 'Выручка', checks: 'Чеков', avgCheck: 'Средний чек',
qty: 'Порций', discount: 'Скидка',
  cost: 'Себестоимость', margin: 'Маржа', marginPct: 'Маржа, %',
  share: 'Доля, %',
};

/** Меры, которым нужны позиции чека, а не только сам чек. */
export const ITEM_LEVEL: Dimension[] = ['category', 'product'];
export const ITEM_MEASURES: Measure[] = ['qty', 'cost', 'margin', 'marginPct'];

export const BuildReportRequest = z.object({
  from: z.string(),
  to: z.string(),
  /** до трёх измерений: глубже человек уже не читает таблицу */
  dimensions: z.array(Dimension).min(1).max(3),
  measures: z.array(Measure).min(1).max(8),
  locationId: z.string().optional(),
  /** сортировка по мере, по убыванию */
  sortBy: Measure.optional(),
  limit: z.number().int().min(5).max(500).default(200),
});
export type BuildReportRequest = z.infer<typeof BuildReportRequest>;

export const ReportRow = z.object({
  keys: z.array(z.string()),
  values: z.record(z.number()),
});
export type ReportRow = z.infer<typeof ReportRow>;

export const ReportResult = z.object({
  dimensions: z.array(Dimension),
  measures: z.array(Measure),
  rows: z.array(ReportRow),
  totals: z.record(z.number()),
  /** сколько строк отброшено лимитом — молчать об этом нельзя */
  truncated: z.number(),
  /** предупреждение о малой выборке: «данных мало, выводы преждевременны» */
  warning: z.string().nullable(),
  from: z.string(),
  to: z.string(),
  ms: z.number(),
});
export type ReportResult = z.infer<typeof ReportResult>;

export const SaveTemplateRequest = z.object({
  name: z.string().min(2).max(60),
  dimensions: z.array(Dimension).min(1).max(3),
  measures: z.array(Measure).min(1).max(8),
  sortBy: Measure.optional(),
  shared: z.boolean().default(false),
});
export type SaveTemplateRequest = z.infer<typeof SaveTemplateRequest>;

export const ReportTemplate = z.object({
  id: z.string(),
  name: z.string(),
  dimensions: z.array(Dimension),
  measures: z.array(Measure),
  sortBy: Measure.nullable(),
  shared: z.boolean(),
  authorName: z.string(),
  mine: z.boolean(),
});
export type ReportTemplate = z.infer<typeof ReportTemplate>;
